import { useTheme } from "../theme/ThemeProvider";
import { useUnits } from "../lib/units";
import { logout } from "../api/client";
import type { CurrentUser } from "../types/ride";
import { ThemeToggle } from "./ThemeToggle";
import { ApiTokensPanel } from "./ApiTokensPanel";
import { Dialog } from "./Dialog";

export function SettingsDialog({ open, onClose, user }: { open: boolean; onClose: () => void; user: CurrentUser }) {
  const { preference } = useTheme();
  const { system, setSystem } = useUnits();

  async function handleLogout() {
    try {
      const { redirectUrl } = await logout();
      window.location.href = redirectUrl;
    } catch {
      window.location.href = "/";
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Settings" widthClassName="max-w-xl">
      <div className="space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-night-800 dark:bg-night-900">
          <h3 className="mb-1 font-medium">Account</h3>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500 dark:text-night-400">{user.name ?? user.email ?? "Signed in via Authentik"}</p>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-night-400 dark:hover:bg-night-800"
            >
              Sign out
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-night-800 dark:bg-night-900">
          <h3 className="mb-2 font-medium">Appearance</h3>
          <p className="mb-3 text-sm text-slate-500 dark:text-night-400">
            Current: <span className="font-medium capitalize">{preference}</span>
          </p>
          <ThemeToggle />
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-night-800 dark:bg-night-900">
          <h3 className="mb-2 font-medium">Units</h3>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 dark:border-night-700 dark:bg-night-900">
            {(["metric", "imperial"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setSystem(opt)}
                aria-pressed={system === opt}
                className={`rounded-md px-3 py-1.5 text-sm capitalize transition ${
                  system === opt
                    ? "bg-brand-600 text-white"
                    : "text-slate-500 hover:bg-slate-100 dark:text-night-400 dark:hover:bg-night-800"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </section>

        <ApiTokensPanel />
      </div>
    </Dialog>
  );
}
